// Use @reduxjs/toolkit/query/react as it provides a React-specific entry point
// that automatically generates hooks corresponding to the defined endpoints

import { HttpMethods } from 'msw';

import { apiSlice } from '@/API/api.slice';
import { Endpoints } from '@/constants/api';
import { uiSlice } from '@/store/slices/ui.slice';
import Department from '@/types/department.types';

interface GetDepartmentsArgs {
  excludeMRC?: boolean;
}

export const departmentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDepartments: builder.query<Department[], GetDepartmentsArgs | void>({
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // Only the no-arg call is the shared, app-wide cache entry every dropdown subscribes to; the
        // app-wide loading indicator toggle is a single flip and would desync if a second, differently
        // keyed cache entry (excludeMRC) also flipped it while the first was still in flight.
        if (arg?.excludeMRC) {
          await queryFulfilled.catch(() => undefined);
          return;
        }

        try {
          dispatch(uiSlice.actions.toggleIsLoadingDepartments());
          await queryFulfilled;
        } catch {
          // Callers which need to surface the failure read the query's own error state (see
          // ViewAccessControls); this catch only exists so the finally can run. The loading flag is a toggle,
          // so this endpoint must stay a single cache entry - two concurrent department queries would flip it
          // out of step and leave the app-wide loading indicator stuck.
        } finally {
          dispatch(uiSlice.actions.toggleIsLoadingDepartments());
        }
      },
      query: (arg) => {
        return {
          body: {
            templates: [],
            // eslint-disable-next-line camelcase
            ...(arg?.excludeMRC && { exclude_mrc: true }),
          },
          method: HttpMethods.POST,
          url: `${Endpoints.Departments}`,
        };
      },
      transformResponse: (response: Department[]) => {
        if(!response) return [];

        response.sort((a, b) => a.name.localeCompare(b.name));
        return response;
      },
    }),
  }),
});

export const { useGetDepartmentsQuery } = departmentsApi;
